/**
 * Donut charts for .chart-cards — reads data-chart-percent (0–100) per chart.
 * Optional: data-chart-config on .chart-cards root as JSON array of { percent, label?, tags? }.
 */

// Normalized 0–100 viewBox — rings fill the SVG (see chart-cards.html)
const CHART_CX = 50;
const CHART_CY = 50;
const CHART_RADIUS = 45;
const CIRCUMFERENCE = 2 * Math.PI * CHART_RADIUS;
const DURATION_MS = 600;
const ARC_START_RAD = -Math.PI / 2;

const activeAnimations = new WeakMap();
const scheduledCharts = new WeakSet();

function isLoaderComplete() {
  const loader = document.getElementById("site-loader");
  if (!loader) return true;
  return (
    document.body.classList.contains("site-loader-hidden") ||
    loader.classList.contains("is-hidden")
  );
}

function clampPercent(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return 0;
  return Math.min(100, Math.max(0, n));
}

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function easeOutCubic(t) {
  return 1 - (1 - t) ** 3;
}

function arcAngleRad(percent) {
  return ARC_START_RAD + (percent / 100) * 2 * Math.PI;
}

function knobPoint(percent) {
  const angle = arcAngleRad(percent);
  return {
    cx: CHART_CX + CHART_RADIUS * Math.cos(angle),
    cy: CHART_CY + CHART_RADIUS * Math.sin(angle),
  };
}

function progressOffset(percent) {
  return CIRCUMFERENCE * (1 - percent / 100);
}

function setKnobPosition(knob, percent) {
  const { cx, cy } = knobPoint(percent);
  knob.style.left = `${cx}%`;
  knob.style.top = `${cy}%`;
}

function setProgress(progress, percent) {
  progress.setAttribute("stroke-dasharray", String(CIRCUMFERENCE));
  progress.setAttribute("stroke-dashoffset", String(progressOffset(percent)));
}

function cancelAnimation(chartEl) {
  const id = activeAnimations.get(chartEl);
  if (id != null) {
    cancelAnimationFrame(id);
    activeAnimations.delete(chartEl);
  }
}

function animateChart(chartEl, percent) {
  const progress = chartEl.querySelector(".chart-cards__progress");
  const knob = chartEl.querySelector(".chart-cards__knob");
  const valueEl = chartEl.querySelector("[data-chart-value]");

  if (!progress || !knob) return;

  cancelAnimation(chartEl);

  progress.style.transition = "none";

  const fromPercent = 0;
  const toPercent = percent;
  const start = performance.now();

  function frame(now) {
    const t = Math.min(1, (now - start) / DURATION_MS);
    const eased = easeOutCubic(t);
    const current = fromPercent + (toPercent - fromPercent) * eased;

    setProgress(progress, current);
    setKnobPosition(knob, current);

    if (valueEl) {
      valueEl.textContent = `${Math.round(current)}%`;
    }

    if (t < 1) {
      activeAnimations.set(chartEl, requestAnimationFrame(frame));
    } else {
      setProgress(progress, toPercent);
      setKnobPosition(knob, toPercent);
      if (valueEl) {
        valueEl.textContent = `${Math.round(toPercent)}%`;
      }
      activeAnimations.delete(chartEl);
    }
  }

  setProgress(progress, 0);
  setKnobPosition(knob, 0);
  activeAnimations.set(chartEl, requestAnimationFrame(frame));
}

function renderChart(chartEl, percent, { animate = false } = {}) {
  const progress = chartEl.querySelector(".chart-cards__progress");
  const knob = chartEl.querySelector(".chart-cards__knob");
  const valueEl = chartEl.querySelector("[data-chart-value]");

  if (!progress || !knob) return;

  cancelAnimation(chartEl);

  if (valueEl) {
    valueEl.textContent = `${Math.round(percent)}%`;
  }

  if (animate && !prefersReducedMotion()) {
    requestAnimationFrame(() => animateChart(chartEl, percent));
    return;
  }

  progress.style.transition = "";
  setProgress(progress, percent);
  setKnobPosition(knob, percent);
}

function scheduleChartReveal(chartEl, percent) {
  if (scheduledCharts.has(chartEl)) return;
  scheduledCharts.add(chartEl);

  renderChart(chartEl, 0, { animate: false });

  let inView = false;
  let loaderDone = isLoaderComplete();
  let played = false;
  let loaderObserver = null;

  const cleanup = () => {
    intersectionObserver.disconnect();
    loaderObserver?.disconnect();
  };

  const play = () => {
    if (played || !inView || !loaderDone) return;
    played = true;
    cleanup();
    renderChart(chartEl, percent, { animate: true });
  };

  const intersectionObserver = new IntersectionObserver(
    ([entry]) => {
      inView = Boolean(entry?.isIntersecting);
      play();
    },
    { threshold: 0.15, rootMargin: "0px 0px -30% 0px" },
  );

  intersectionObserver.observe(chartEl);

  if (!loaderDone) {
    loaderObserver = new MutationObserver(() => {
      if (!isLoaderComplete()) return;
      loaderDone = true;
      play();
    });

    loaderObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    const loader = document.getElementById("site-loader");
    if (loader) {
      loaderObserver.observe(loader, {
        attributes: true,
        attributeFilter: ["class"],
      });
    }
  }
}

function applyConfigToCards(root, config) {
  const cards = root.querySelectorAll(".chart-cards__card");
  config.forEach((item, index) => {
    const card = cards[index];
    if (!card || item == null) return;

    const percent = clampPercent(item.percent ?? item.value);
    const chart = card.querySelector(".chart-cards__chart");
    if (chart) {
      chart.setAttribute("data-chart-percent", String(percent));
      scheduleChartReveal(chart, percent);
    }

    if (item.label && card.querySelector(".chart-cards__title")) {
      const title = card.querySelector(".chart-cards__title");
      const lines = String(item.label).split(/\s*\/\s*|\n/).filter(Boolean);
      title.innerHTML = lines.map((line) => `<span>${line}</span>`).join("");
    }

    if (Array.isArray(item.tags)) {
      const list = card.querySelector(".chart-cards__tags");
      if (list) {
        list.innerHTML = item.tags
          .map((tag) => {
            const text = String(tag);
            const parts = text.split(/\n/).filter(Boolean);
            if (parts.length > 1) {
              return `<li class="chart-cards__tag">${parts.map((p) => `<span>${p}</span>`).join("")}</li>`;
            }
            return `<li class="chart-cards__tag"><span>${text}</span></li>`;
          })
          .join("");
      }
    }
  });
}

export function initChartCards() {
  document.querySelectorAll(".chart-cards").forEach((root) => {
    let configApplied = false;
    const rawConfig = root.getAttribute("data-chart-config");

    if (rawConfig) {
      try {
        const config = JSON.parse(rawConfig);
        if (Array.isArray(config)) {
          applyConfigToCards(root, config);
          configApplied = true;
        }
      } catch {
        // ignore invalid JSON; fall back to per-chart attributes
      }
    }

    if (!configApplied) {
      root.querySelectorAll(".chart-cards__chart[data-chart-percent]").forEach((chartEl) => {
        const percent = clampPercent(chartEl.getAttribute("data-chart-percent"));
        scheduleChartReveal(chartEl, percent);
      });
    }
  });
}
